import React from "react";
import { Plane, Clock, Ban } from "lucide-react";
import { fmtPKT } from "./verify";

const STATUS = {
  waiting: ["Waiting", "bg-yellow-500/15 text-yellow-400 border-yellow-500/40"],
  running: ["Running", "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"],
  crashed: ["Crashed", "bg-red-500/15 text-red-400 border-red-500/40"],
  completed: ["Completed", "bg-slate-500/15 text-slate-300 border-slate-500/40"],
  closed: ["Game Closed", "bg-slate-500/15 text-slate-300 border-slate-500/40"],
};

const CLOSED_MSG = {
  disabled: "The game is currently disabled by admin.",
  paused: "The game session is paused. Please check back soon.",
  stopped: "The game session has been stopped.",
  postponed: "The game session has been postponed.",
  out_of_hours: "The game is outside its daily operating hours.",
  out_of_schedule: "The game is outside its scheduled dates.",
};

function Curve({ elapsed, multiplier, growth, show }) {
  const W = 400, H = 220;
  const tMax = Math.max(8, elapsed * 1.15);
  const mMax = Math.max(2, multiplier * 1.2);
  const pt = (t) => {
    const m = Math.exp(growth * t);
    return [20 + (t / tMax) * (W - 60), H - 20 - ((m - 1) / (mMax - 1)) * (H - 60)];
  };
  const n = 40;
  const pts = Array.from({ length: n + 1 }, (_, i) => pt((elapsed * i) / n));
  const d = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const [px, py] = pts[n];
  const [qx, qy] = pts[Math.max(0, n - 2)];
  const angle = (Math.atan2(py - qy, px - qx) * 180) / Math.PI;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id="avFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#C9A227" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#C9A227" stopOpacity="0" />
        </linearGradient>
      </defs>
      {show && elapsed > 0 && (
        <>
          <path d={`${d} L${px},${H - 20} L20,${H - 20} Z`} fill="url(#avFill)" />
          <path d={d} stroke="#E8C766" strokeWidth="3" fill="none" strokeLinecap="round" />
          <g transform={`translate(${px},${py}) rotate(${angle})`}>
            <foreignObject x="-18" y="-18" width="36" height="36">
              <Plane className="w-9 h-9 text-[#F5E9C2] drop-shadow-[0_0_10px_rgba(201,162,39,0.9)]" style={{ transform: "rotate(45deg)" }} />
            </foreignObject>
          </g>
        </>
      )}
    </svg>
  );
}

export const AviatorStage = ({ state, multiplier, elapsed, countdown }) => {
  const rnd = state?.round;
  const sched = state?.schedule;
  const key = rnd ? rnd.status : "closed";
  const [label, cls] = STATUS[key] || STATUS.closed;
  const crashed = rnd?.status === "crashed" || rnd?.status === "completed";
  return (
    <div className="ec-card ec-dark relative overflow-hidden h-72 sm:h-96" data-testid="aviator-stage">
      <div className="absolute inset-0 opacity-30 [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:40px_40px]" />
      <div className="absolute top-4 start-4 flex items-center gap-2 z-10">
        <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border ${cls}`} data-testid="aviator-status">{label}</span>
        {rnd && <span className="text-xs text-gray-500" data-testid="aviator-round-no">Round #{rnd.round_no}</span>}
      </div>
      {rnd && <span className="absolute top-4 end-4 text-xs text-gray-500 z-10" data-testid="aviator-players">{rnd.players} players</span>}
      <Curve elapsed={rnd?.status === "running" ? elapsed : 0} multiplier={multiplier} growth={rnd?.growth_rate || 0.1} show={rnd?.status === "running"} />
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-4 text-center">
        {!rnd && (
          <div data-testid="aviator-closed">
            <Ban className="w-12 h-12 text-gray-500 mx-auto mb-3" />
            <p className="font-head text-3xl sm:text-4xl font-extrabold text-white">Game Closed</p>
            <p className="text-gray-400 text-sm mt-2">{CLOSED_MSG[sched?.closed_reason] || "Participation is not available right now."}</p>
            {sched?.postpone_note && <p className="text-amber-400 text-sm mt-2" data-testid="aviator-postpone-note">{sched.postpone_note}</p>}
            {sched?.next_open_at && <p className="text-orange-400 text-sm mt-2" data-testid="aviator-next-open">Opens: {fmtPKT(sched.next_open_at)}</p>}
          </div>
        )}
        {rnd?.status === "waiting" && (
          <div data-testid="aviator-countdown">
            <Clock className="w-10 h-10 text-yellow-400 mx-auto mb-2 animate-pulse" />
            <p className="text-gray-400 text-sm uppercase tracking-widest">Next flight in</p>
            <p className="font-head text-6xl font-extrabold text-gradient">{countdown.toFixed(1)}s</p>
            <p className="text-gray-500 text-xs mt-2">Place your amount now</p>
          </div>
        )}
        {rnd?.status === "running" && (
          <p className="font-head text-6xl sm:text-7xl font-extrabold text-white drop-shadow-[0_0_20px_rgba(255,184,0,0.5)]" data-testid="aviator-multiplier">{multiplier.toFixed(2)}x</p>
        )}
        {crashed && (
          <div data-testid="aviator-crashed">
            <p className="text-red-400 text-sm uppercase tracking-widest font-bold">Flew away!</p>
            <p className="font-head text-6xl sm:text-7xl font-extrabold text-red-500" data-testid="aviator-crash-point">{Number(rnd.crash_point).toFixed(2)}x</p>
          </div>
        )}
      </div>
      {rnd && <p className="absolute bottom-2 start-4 end-4 text-[10px] text-gray-600 truncate z-10 keep-ltr" data-testid="aviator-commit-hash">Commitment: {rnd.commit_hash}</p>}
    </div>
  );
};
